import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ApiError } from '@/api/client';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Shown when a user is signed in but has no active organisation.
 *
 * This happens by design: login refuses to guess for anyone belonging to more
 * than one organisation, because guessing would silently scope their work to
 * the wrong company. Without this screen the consequence was a dashboard with
 * no data and no permissions, which reads as a broken application rather than
 * a decision waiting to be made.
 */
export function ChooseOrganizationPage() {
  const { memberships, switchOrganization, user } = useAuth();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choose = async (organizationId: string) => {
    setError(null);
    setPending(organizationId);

    try {
      await switchOrganization(organizationId);
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Could not open that organisation.');
    } finally {
      setPending(null);
    }
  };

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography variant="h1">Choose an organisation</Typography>
            <Typography variant="body2" color="text.secondary">
              Signed in as {user?.email}. Everything you do is scoped to the organisation you pick,
              so we do not choose for you.
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {memberships.length === 0 ? (
            <Alert severity="warning" variant="outlined">
              Your account does not belong to any organisation yet. Ask an administrator to invite
              you, or create your own workspace.
            </Alert>
          ) : (
            <List disablePadding>
              {memberships.map((membership) => (
                <ListItemButton
                  key={membership.id}
                  disabled={pending !== null}
                  onClick={() => void choose(membership.organization.id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon>
                    {pending === membership.organization.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <BusinessIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={membership.organization.name}
                    secondary={membership.role_label}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={membership.organization.default_currency}
                    sx={{ mr: 1 }}
                  />
                  <ChevronRightIcon fontSize="small" color="disabled" />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
